import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@/providers/RouterShim';
import { useVehicleDrawer } from '../../data/entityDrawer/useVehicleDrawer';
import Badge from '../../styled-components/components/Badge';
import { Form, FormGroup } from '../../styled-components/components/Form';
import InputStaticValue from '../../styled-components/components/InputStaticValue';
import { RouterLink } from '../../styled-components/components/Link';
import { ErrorContainer, LoadingContainer } from './styled';

interface VehicleDrawerContentProps {
  entityId: string;
  onClose: () => void;
}

/**
 * Vehicle drawer content component
 * Displays vehicle information in the drawer
 */
export const VehicleDrawerContent: React.FC<VehicleDrawerContentProps> = ({
  entityId,
  onClose,
}) => {
  const [vehicleData, { isLoading, isError }] = useVehicleDrawer(entityId);
  const { branchId } = useParams();
  const { t } = useTranslation(['common', 'glossary']);

  if (isLoading) {
    return <LoadingContainer>{t('drawer.loadingVehicleData', { ns: 'common' })}</LoadingContainer>;
  }

  if (isError || !vehicleData?.vehicleAd) {
    return <ErrorContainer>{t('drawer.couldNotLoadVehicleData', { ns: 'common' })}</ErrorContainer>;
  }

  const { vehicleAd } = vehicleData;
  const vehicle = vehicleAd.vehicle;

  // Format price
  const price = vehicleAd.ad?.price;
  const formattedPrice = price
    ? new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(price)
    : null;

  // Get status tone for badge
  const getStatusTone = (status: string | null | undefined) => {
    if (!status) return 'disabled';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('available') || statusLower.includes('tillgänglig')) return 'success';
    if (statusLower.includes('sold') || statusLower.includes('såld')) return 'disabled';
    if (statusLower.includes('reserved') || statusLower.includes('reserverad')) return 'warning';
    return 'info';
  };

  return (
    <Form>
      {vehicleAd.status && (
        <FormGroup>
          <Badge tone={getStatusTone(vehicleAd.status)}>{vehicleAd.status}</Badge>
        </FormGroup>
      )}

      {formattedPrice && (
        <FormGroup>
          <InputStaticValue label={t('price', { ns: 'glossary' })} value={formattedPrice} />
        </FormGroup>
      )}

      {vehicle.registrationNumber && (
        <FormGroup $width="half">
          <InputStaticValue
            label={t('registrationNumber', { ns: 'glossary' })}
            value={vehicle.registrationNumber}
          />
        </FormGroup>
      )}

      {vehicle.manufacturer && vehicle.modelName && (
        <FormGroup $width="half">
          <InputStaticValue
            label={t('modelName', { ns: 'glossary' })}
            value={`${vehicle.manufacturer} ${vehicle.modelName}`}
          />
        </FormGroup>
      )}

      {vehicle.modelYear && (
        <FormGroup $width="half">
          <InputStaticValue
            label={t('modelYear', { ns: 'glossary' })}
            value={String(vehicle.modelYear)}
          />
        </FormGroup>
      )}

      {vehicle.mileage && (
        <FormGroup $width="half">
          <InputStaticValue
            label={t('mileage', { ns: 'glossary' })}
            value={`${vehicle.mileage.toLocaleString('sv-SE')} km`}
          />
        </FormGroup>
      )}

      <FormGroup>
        <RouterLink
          to={`/${branchId}/vehicles/administrate-vehicles/${entityId}`}
          onClick={onClose}
        >
          {t('drawer.openFullView', { ns: 'common' })}
        </RouterLink>
      </FormGroup>
    </Form>
  );
};
